import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Eye,
  MapPin,
  MessageSquare,
  MousePointerClick,
  Phone,
  Star,
  TrendingDown,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { SectionHeader } from '@/components/charts/SectionHeader';
import { EmptyState } from '@/components/charts/EmptyState';
import { MotionCard } from '@/components/motion/MotionCard';
import { useOutlets } from '@/hooks/useOutlets';
import { useGoogleReviews, useListingHealth } from '@/hooks/useGmb';
import { formatNumberCompact } from '@/lib/format';
import { cn } from '@/lib/utils';

export function ReviewsPage() {
  const health = useListingHealth();
  const [filter, setFilter] = useState<'all' | 'unreplied' | 'negative'>('unreplied');
  const reviews = useGoogleReviews({
    onlyUnreplied: filter === 'unreplied',
    onlyNegative: filter === 'negative',
    limit: 100,
  });

  const totals = useMemo(() => {
    const rows = health.data ?? [];
    return {
      outlets: rows.length,
      totalReviews: rows.reduce((s, r) => s + (r.total_reviews ?? 0), 0),
      totalViews: rows.reduce((s, r) => s + (r.views_total ?? 0), 0),
      totalActions: rows.reduce((s, r) => s + (r.actions_total ?? 0), 0),
      unreplied: rows.reduce((s, r) => s + (r.unreplied_count ?? 0), 0),
      avgRating: avgWeighted(
        rows.map((r) => ({ v: r.avg_star_rating ?? 0, w: r.total_reviews ?? 0 })),
      ),
    };
  }, [health.data]);

  return (
    <div className="space-y-8">
      <SectionHeader
        title={
          <span className="flex items-center gap-2">
            <Star className="h-5 w-5 text-amber-500" />
            Google reviews & listing health
          </span>
        }
        description={
          health.data
            ? `${totals.outlets} outlets · last 30d performance, lifetime ratings`
            : 'Google Business Profile — lifetime ratings + last-30d traffic.'
        }
      />

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <SummaryTile
          label="Avg rating"
          value={totals.avgRating > 0 ? totals.avgRating.toFixed(1) : '—'}
          suffix="★"
          icon={<Star className="h-3.5 w-3.5" />}
          loading={health.isLoading}
        />
        <SummaryTile
          label="Total reviews"
          value={formatNumberCompact(totals.totalReviews)}
          icon={<MessageSquare className="h-3.5 w-3.5" />}
          loading={health.isLoading}
        />
        <SummaryTile
          label="Views (30d)"
          value={formatNumberCompact(totals.totalViews)}
          icon={<Eye className="h-3.5 w-3.5" />}
          loading={health.isLoading}
        />
        <SummaryTile
          label="Unreplied"
          value={formatNumberCompact(totals.unreplied)}
          icon={<MessageSquare className="h-3.5 w-3.5" />}
          tone={totals.unreplied > 0 ? 'warning' : 'neutral'}
          loading={health.isLoading}
        />
      </div>

      <MotionCard>
        <Card>
          <CardHeader>
            <CardTitle>Listing health by outlet</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <ListingHealthTable health={health.data ?? []} loading={health.isLoading} />
          </CardContent>
        </Card>
      </MotionCard>

      <MotionCard delay={0.05}>
        <Card>
          <CardHeader className="flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle>Reviews</CardTitle>
            <div className="inline-flex rounded-md border border-border/60 bg-card p-0.5 text-xs">
              {(['unreplied', 'negative', 'all'] as const).map((id) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setFilter(id)}
                  className={cn(
                    'rounded px-2.5 py-1 transition-colors',
                    filter === id
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {id === 'unreplied' ? 'Unreplied' : id === 'negative' ? 'Negative (≤2★)' : 'All recent'}
                </button>
              ))}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {reviews.isLoading ? (
              <div className="space-y-2 p-4">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} className="h-20 w-full" />
                ))}
              </div>
            ) : (reviews.data ?? []).length === 0 ? (
              <div className="p-6">
                <EmptyState
                  title={
                    filter === 'unreplied'
                      ? 'No unreplied reviews'
                      : filter === 'negative'
                        ? 'No negative reviews'
                        : 'No reviews'
                  }
                  description={
                    filter === 'unreplied'
                      ? 'Every review has a reply — great work.'
                      : 'Last 90 days of imports.'
                  }
                />
              </div>
            ) : (
              <ul className="divide-y divide-border/40">
                {(reviews.data ?? []).map((r) => (
                  <ReviewRow key={r.review_id} review={r} />
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </MotionCard>
    </div>
  );
}

function avgWeighted(items: { v: number; w: number }[]): number {
  let sum = 0;
  let totalW = 0;
  for (const it of items) {
    sum += it.v * it.w;
    totalW += it.w;
  }
  return totalW > 0 ? sum / totalW : 0;
}

function SummaryTile({
  label,
  value,
  suffix,
  icon,
  tone,
  loading,
}: {
  label: string;
  value: string;
  suffix?: string;
  icon: React.ReactNode;
  tone?: 'neutral' | 'warning';
  loading?: boolean;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-1.5 text-2xs font-normal text-muted-foreground">
          {icon}
          {label}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-8 w-20" />
        ) : (
          <div
            className={cn(
              'font-semibold tabular-nums text-2xl',
              tone === 'warning' && value !== '0' && 'text-amber-500',
            )}
          >
            {value}
            {suffix && <span className="ml-0.5 text-base text-muted-foreground">{suffix}</span>}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ListingHealthTable({
  health,
  loading,
}: {
  health: ReturnType<typeof useListingHealth>['data'] extends infer T ? T : never;
  loading: boolean;
}) {
  const { data: outlets = [] } = useOutlets();
  const nameOf = (id: string) => outlets.find((o) => o.id === id)?.name ?? id;

  if (loading) {
    return (
      <div className="space-y-2 p-4">
        {[0, 1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }
  if (!health || health.length === 0) {
    return (
      <div className="p-6">
        <EmptyState
          title="No GMB data"
          description="Listing health hasn't synced. Run the GMB sync once it's wired."
        />
      </div>
    );
  }

  const sorted = [...health].sort((a, b) => (b.views_total ?? 0) - (a.views_total ?? 0));

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="border-b border-border/60 bg-muted/30 text-2xs uppercase text-muted-foreground">
          <tr>
            <th className="px-3 py-2 text-left font-medium">Outlet</th>
            <th className="px-3 py-2 text-right font-medium">Rating</th>
            <th className="px-3 py-2 text-right font-medium">Reviews</th>
            <th className="px-3 py-2 text-right font-medium">Views (30d)</th>
            <th className="px-3 py-2 text-right font-medium">Actions (30d)</th>
            <th className="px-3 py-2 text-right font-medium">Unreplied</th>
            <th className="px-3 py-2 text-right font-medium">Reply time</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border/40">
          {sorted.map((row) => {
            const rating = row.avg_star_rating ?? 0;
            const ratingTone =
              rating === 0
                ? 'text-muted-foreground'
                : rating < 3.5
                  ? 'text-rose-500'
                  : rating < 4.2
                    ? 'text-amber-500'
                    : 'text-emerald-500';
            return (
              <tr key={row.outlet_id} className="hover:bg-muted/30">
                <td className="px-3 py-2">
                  <Link to={`/outlets/${row.outlet_id}`} className="font-medium hover:underline">
                    {nameOf(row.outlet_id)}
                  </Link>
                </td>
                <td className={cn('px-3 py-2 text-right font-mono tabular-nums', ratingTone)}>
                  {rating > 0 ? `${rating.toFixed(1)} ★` : '—'}
                </td>
                <td className="px-3 py-2 text-right font-mono tabular-nums">
                  {row.total_reviews != null ? formatNumberCompact(row.total_reviews) : '—'}
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="font-mono tabular-nums">
                    {formatNumberCompact(row.views_total ?? 0)}
                  </div>
                  <div className="text-2xs text-muted-foreground">
                    <MapPin className="mr-0.5 inline h-2.5 w-2.5" />
                    {formatNumberCompact(row.views_maps ?? 0)} Maps
                  </div>
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="font-mono tabular-nums">
                    {formatNumberCompact(row.actions_total ?? 0)}
                  </div>
                  <div className="text-2xs text-muted-foreground">
                    <Phone className="mr-0.5 inline h-2.5 w-2.5" />
                    {row.actions_phone ?? 0}
                    {' · '}
                    <MousePointerClick className="mx-0.5 inline h-2.5 w-2.5" />
                    {row.actions_directions ?? 0}
                  </div>
                </td>
                <td
                  className={cn(
                    'px-3 py-2 text-right font-mono tabular-nums',
                    (row.unreplied_count ?? 0) > 0 ? 'text-amber-500' : 'text-muted-foreground',
                  )}
                >
                  {row.unreplied_count ?? 0}
                </td>
                <td className="px-3 py-2 text-right text-2xs text-muted-foreground">
                  {row.avg_reply_minutes != null && row.avg_reply_minutes > 0
                    ? formatReplyTime(row.avg_reply_minutes)
                    : '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function formatReplyTime(minutes: number): string {
  if (minutes < 60) return `${Math.round(minutes)}m`;
  const h = minutes / 60;
  if (h < 24) return `${h.toFixed(1)}h`;
  const d = h / 24;
  return `${d.toFixed(1)}d`;
}

function ReviewRow({
  review,
}: {
  review: {
    review_id: string;
    outlet_id: string;
    star_rating: number;
    reviewer_name: string | null;
    comment: string | null;
    reply_comment: string | null;
    created_at: string;
    reply_at: string | null;
  };
}) {
  const { data: outlets = [] } = useOutlets();
  const outletName = outlets.find((o) => o.id === review.outlet_id)?.name ?? review.outlet_id;
  const rating = review.star_rating;
  const tone = rating <= 2 ? 'text-rose-500' : rating === 3 ? 'text-amber-500' : 'text-emerald-500';
  const isUnreplied = !review.reply_comment || review.reply_comment === '';

  return (
    <li className="p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className={cn('font-mono font-semibold', tone)}>
              {'★'.repeat(rating)}
              {'☆'.repeat(5 - rating)}
            </span>
            <span className="font-medium">{review.reviewer_name || 'Anonymous'}</span>
            <Link to={`/outlets/${review.outlet_id}`} className="text-2xs text-muted-foreground hover:underline">
              {outletName}
            </Link>
            <span className="text-2xs text-muted-foreground">{review.created_at}</span>
            {isUnreplied && (
              <Badge variant="muted" className="bg-amber-500/15 text-amber-700 dark:text-amber-400">
                Unreplied
              </Badge>
            )}
            {rating <= 2 && (
              <Badge variant="muted" className="bg-rose-500/15 text-rose-600 dark:text-rose-400">
                <TrendingDown className="mr-1 h-3 w-3" />
                Negative
              </Badge>
            )}
          </div>
          {review.comment && (
            <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-foreground/90">
              {review.comment}
            </p>
          )}
          {review.reply_comment && (
            <div className="mt-2 rounded-md border-l-2 border-violet-500/50 bg-violet-500/5 px-3 py-2 text-2xs leading-relaxed text-muted-foreground">
              <div className="mb-1 font-medium text-violet-600 dark:text-violet-400">Your reply</div>
              <p className="whitespace-pre-line">{review.reply_comment}</p>
            </div>
          )}
        </div>
      </div>
    </li>
  );
}
